import { useState, useEffect, useCallback } from 'react';
import { auth } from '../firebase_utils/firebase';
import { getUserCredits } from '../firebase_utils/firebaseUtils';
import { 
  checkCreditsForFeature, 
  spendCreditsForFeature 
} from '../firebase_utils/usageUtils';

// Create a custom event for credit updates
const CREDITS_UPDATED_EVENT = 'creditsUpdated';

// Function to emit the credits updated event
export const emitCreditsUpdated = () => {
  const event = new CustomEvent(CREDITS_UPDATED_EVENT);
  window.dispatchEvent(event);
};

/**
 * Custom hook to manage user credits and feature usage
 * @returns {Object} Credits information and methods
 */
const useCredits = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [credits, setCredits] = useState(0);

  // Load the user's credits
  const loadCredits = useCallback(async () => {
    try {
      setLoading(true);
      if (!auth.currentUser) {
        setCredits(0);
        return;
      }
      
      const userCredits = await getUserCredits(auth.currentUser.uid);
      setCredits(userCredits);
      setError(null);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  // Load credits on mount and auth state change
  useEffect(() => {
    loadCredits();
    
    // Set up listener for auth state changes
    const unsubscribe = auth.onAuthStateChanged(() => {
      loadCredits();
    });

    // Listen for credits updated events
    const handleCreditsUpdated = () => {
      loadCredits();
    };
    window.addEventListener(CREDITS_UPDATED_EVENT, handleCreditsUpdated);
    
    return () => {
      unsubscribe();
      window.removeEventListener(CREDITS_UPDATED_EVENT, handleCreditsUpdated);
    };
  }, [loadCredits]);

  // Check if the user can use a specific feature
  const canUseFeature = useCallback(async (feature) => {
    try {
      if (!auth.currentUser) {
        return { allowed: false, credits: 0, cost: 0 };
      }
      
      const result = await checkCreditsForFeature(auth.currentUser.uid, feature);
      return result;
    } catch (err) {
      setError(err.message);
      return { allowed: false, credits, cost: 0 };
    }
  }, [credits]);

  // Use a feature (spend credits) - modified to avoid hooks in callbacks
  const useFeature = useCallback(async (feature) => {
    try {
      if (!auth.currentUser) {
        return false;
      }
      
      // Direct function call to a regular function, not a hook
      const success = await spendCreditsForFeature(auth.currentUser.uid, feature);
      
      if (success) {
        // Reload credits after successful usage
        loadCredits();
        // Emit the credits updated event
        emitCreditsUpdated();
      }
      
      return success;
    } catch (err) {
      setError(err.message);
      return false;
    }
  }, [loadCredits]);

  return {
    loading,
    error,
    credits,
    canUseFeature,
    useFeature,
    refreshCredits: loadCredits
  };
};

export default useCredits; 